import React, { useMemo, useState } from "react";
import ajaxConfig from "../../../util/remote/ajaxConfig";
import { useConfig } from "../../../DataStore/ConfigContext";
import Notification from "../../Common/Notification";
import BsModal from "../../Common/BsModal";
import TextInput from "../../Common/TextInput";

const NewKeyModal = () => {
  const [username, setUsername] = useState("");
  const [description, setDescription] = useState("");

  const [showSecret, setShowSecret] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // { api_key, api_secret, created_at?, ... }
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);

  const { showNewKeyModal, dispatch } = useConfig();

  const canSubmit = useMemo(() => {
    const okUser = String(username || "").trim().length > 3 && String(username).includes("@");
    const okDesc = String(description || "").trim().length >= 5;
    return okUser && okDesc && !loading;
  }, [username, description, loading]);

  const closeModal = () => {
    setNotify(null);
    setCreatedKey(null);
    setShowSecret(false);
    setUsername("");
    setDescription("");
    dispatch({ type: "showNewKeyModal", payload: false });
  };

  const handleCreateKey = async () => {
    setNotify(null);

    const cleanUser = String(username || "").trim();
    const cleanDesc = String(description || "").trim();

    if (!cleanUser || !cleanUser.includes("@")) {
      setNotify(<Notification type="danger" message="A valid username (email) is required." />);
      return;
    }
    if (!cleanDesc || cleanDesc.length < 5) {
      setNotify(<Notification type="danger" message="Description is required (at least 5 characters)." />);
      return;
    }

    setLoading(true);

    const payload = { username: cleanUser, description: cleanDesc };
    const server_response = await ajaxConfig.createKey(payload);

    setLoading(false);

    if (server_response?.status === "OK") {
      const keyData = server_response.details || null;

      // If backend returns the key/secret, show it once
      setCreatedKey(keyData);

    
      setNotify(<Notification type="success" message={server_response.message || "API Key created successfully."} />);
    } else {
      setNotify(
        <Notification
          type="danger"
          message={server_response?.message || "Failed to create API Key. Please try again."}
        />
      );
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      setNotify(<Notification type="success" message="Copied to clipboard." />);
    } catch (e) {
      setNotify(<Notification type="danger" message="Copy failed. Please copy manually." />);
    }
  };

  return (
    <BsModal
      show={showNewKeyModal}
      onClose={closeModal}
      title="Create New API Key"
      size="modal-lg"
      footer={
        createdKey ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Close
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-light" onClick={closeModal} disabled={loading}>
              Cancel
            </button>

            <button className="btn btn-success" disabled={!canSubmit} onClick={handleCreateKey}>
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <i className="fa fa-key mr-2" />
                  Create API Key
                </>
              )}
            </button>
          </>
        )
      }
    >
      {/* Top “hero” strip */}
      <div
        className="mb-3 p-3 rounded sys-info"
      >
        <div className="d-flex align-items-start">
          <div
            className="mr-3 d-flex align-items-center justify-content-center rounded bg-primary text-white"
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
            }}
          >
            <i className="fa fa-plug" style={{ fontSize: 18 }} />
          </div>

          <div className="flex-grow-1">
            <div className="tx-16 font-weight-bold mb-1">
              Connect external services securely
            </div>
            <div className="tx-13">
              Create an API Key for integrations like YEXP, OpenFi, or other systems. Treat the secret like a password.
            </div>

            <div className="mt-2 tx-12">
              <i className="fa fa-shield mr-2" />
              Only show the secret once • Copy and store it safely
            </div>
          </div>
        </div>
      </div>

      {/* If created, show “one-time” key reveal panel */}
      {createdKey ? (
        <div className="p-3 rounded" style={{ border: "1px solid #e9ecef", background: "#f8fafc" }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="font-weight-bold">
              <i className="fa fa-check-circle text-success mr-2" />
              API Key created
            </div>
            <span className="badge badge-success">One-time reveal</span>
          </div>

          <div className="tx-13 text-muted mb-3">
            Copy these credentials now. For security, you may not be able to view the secret again.
          </div>

          <div className="mb-3">
            <div className="tx-12 text-muted mb-1">API Key</div>
            <div className="input-group">
              <input
                className="form-control"
                value={createdKey}
                readOnly
              />
              <div className="input-group-append">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => copyToClipboard(createdKey)}
                  type="button"
                >
                  <i className="fa fa-copy mr-1" /> Copy
                </button>
              </div>
            </div>
          </div>

      
        </div>
      ) : (
        <>
          {/* Form */}
          <div className="mb-3">
            <TextInput
              label="Username"
              placeholder="E.g. schoolaccounts@gmail.com"
              leftIcon="fa fa-user"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />

            <div className="tx-12 text-muted mt-1">
              <i className="fa fa-info-circle mr-1" />
              Use the email/username that identifies a registered eschool accounts user.
            </div>
          </div>

          <div className="mb-2">
            <TextInput
              label="Description"
              placeholder="E.g. Connection to YEXP to link students."
              leftIcon="fa fa-align-left"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />

            <div className="d-flex justify-content-between align-items-center mt-1">
              <div className="tx-12 text-muted">
                <i className="fa fa-tag mr-1" />
                Describe what this key will access.
              </div>
              <span className={`tx-12 ${String(description).length > 120 ? "text-danger" : "text-muted"}`}>
                {String(description).length}/160
              </span>
            </div>
          </div>

          {/* Preview card */}
          <div className="mt-3 p-3 rounded" style={{ border: "1px solid #e9ecef", background: "#f8fafc" }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="font-weight-bold">
                <i className="fa fa-eye mr-2 text-primary" />
                Preview
              </div>
              <span className="badge badge-light">Integration summary</span>
            </div>

            <div className="tx-13 mb-1">
              <span className="text-muted">Username:</span>{" "}
              <span className="font-weight-bold">{username || "—"}</span>
            </div>
            <div className="tx-13">
              <span className="text-muted">Description:</span>{" "}
              <span className="font-weight-bold">{description || "—"}</span>
            </div>

            <div className="mt-3 tx-12 text-muted">
              <i className="fa fa-lock mr-1" />
              Key will be generated server-side and returned once (if your backend is configured to return secrets).
            </div>
          </div>

          {notify}
        </>
      )}

      {!createdKey && loading && (
        <div className="mt-3">
          <i className="fa fa-spinner fa-spin mr-2" />
          Creating your key...
        </div>
      )}

      {!createdKey && notify}
    </BsModal>
  );
};

export default NewKeyModal;
